import React from "react";
import { Edit, Delete, CalendarToday } from "@mui/icons-material";

const TaskList = ({
  title,
  tasks,
  isLoading,
  role,
  users,
  currentUserId,
  handleTaskEdit,
  handleDeleteTask,
  deleteTaskMutation,
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const isToday = (dateStr) => {
    if (!dateStr) return false;
    const taskDate = new Date(dateStr);
    const today = new Date();
    
    return (
      taskDate.getDate() === today.getDate() &&
      taskDate.getMonth() === today.getMonth() &&
      taskDate.getFullYear() === today.getFullYear()
    );
  };

  const displayTasks = tasks || [];

  if (isLoading && displayTasks.length === 0) {
    return (
      <div className="p-6 border-t">
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!displayTasks || displayTasks.length === 0) {
    return (
      <div className="p-6 border-t">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
        <div className="text-center py-8 2xl:py-24 text-gray-500">
          <CalendarToday className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p>No tasks for this date</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 border-t">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>

      {/* Task list */}
      <div className="space-y-3">
        {displayTasks.map((task) => (
          <TaskCard
            key={task._id}
            task={task}
            role={role}
            handleTaskEdit={handleTaskEdit}
            handleDeleteTask={handleDeleteTask}
            deleteTaskMutation={deleteTaskMutation}
            formatDate={formatDate}
            isToday={isToday}
            color={task.leave ? "orange" : "green"}
          />
        ))}
      </div>

      {/* Load More Button with Spinner */}
      {currentPage < totalPages && (
        <div className="flex items-center justify-center py-6">
          {isLoading ? (
            <div className="w-8 h-8 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin"></div>
          ) : (
            <button
              onClick={() => onPageChange(currentPage + 1)}
              className="px-6 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Load More
            </button>
          )}
        </div>
      )}

      {/* End of results message */}
      {currentPage >= totalPages && displayTasks.length > 0 && (
        <div className="flex items-center justify-center py-6">
          <div className="text-sm text-gray-500">You've reached the end</div>
        </div>
      )}
    </div>
  );
};

const TaskCard = ({
  task,
  role,
  handleTaskEdit,
  handleDeleteTask,
  deleteTaskMutation,
  formatDate,
  isToday,
  color,
}) => {
  const colorClasses =
    color === "orange"
      ? "bg-orange-50 border-l-4 border-orange-500"
      : "bg-white border border-gray-200";

  const canEdit = role === "employee" ? isToday(task.date) : role === "admin";
  const canDelete = role === "admin"; 

  return (
    <div
      className={`${colorClasses} p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow`}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h4 className="font-semibold text-gray-900">{task.title}</h4>
            {task.leave && (
              <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                Leave
              </span>
            )}
          
          </div>

          {/* Date */}
          <div className="text-xs text-gray-600 mb-2 flex items-center gap-1">
            <CalendarToday style={{ fontSize: 14 }} />
            <span className="font-medium">{formatDate(task.date)}</span>
            {isToday(task.date) && (
              <span className="ml-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                Today
              </span>
            )}
          </div>

          {/* Description */}
          <div className="text-sm text-gray-700 mb-2">
            {task.description
              ?.split("\n")
              .filter((line) => line.trim())
              .map((line, idx) => (
                <div key={idx} className="flex gap-2 mb-1">
                  <span className="font-semibold min-w-[20px]">{idx + 1}.</span>
                  <span className="flex-1 break-all">{line.trim()}</span>
                </div>
              ))}
          </div>

          {/* Employee info */}
          {task.user && (
            <div className="text-xs text-gray-600 bg-gray-100 inline-block px-2 py-1 rounded">
              <span className="font-medium">👤</span> {task.user.username}
              {task.user.department && (
                <span className="ml-1 text-gray-500">
                  • {task.user.department}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Actions - Only show if user has permissions */}
        {(canEdit || canDelete) && (
          <div className="flex gap-2 ml-4">
            {canEdit && (
              <button
                onClick={() => handleTaskEdit(task)}
                className="text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-50 rounded transition-colors"
                title={role === "employee" && !isToday(task.date) 
                  ? "Can only edit today's tasks" 
                  : "Edit"}
              >
                <Edit fontSize="small" />
              </button>
            )}
            {canDelete && (
              <button
                onClick={() => handleDeleteTask(task._id)}
                disabled={deleteTaskMutation?.isPending}
                className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                title="Delete"
              >
                <Delete fontSize="small" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskList;